import { ToolbarSwitcher } from "@/components/toolbar-switcher";
import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { useGetProxies } from "@/services";
import {
	Button,
	DataTable,
	Icon,
	Typography,
	useDataTableRowSelection,
	useServerTableState,
} from "@st-marketplace/ui";
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { type FC, useCallback, useMemo, useState } from "react";
import ProxiesBatchUpdateDialog from "./ProxiesBatchUpdateDialog";
import ProxiesDeleteAction, {
	ProxiesSingleAction,
} from "./ProxiesDeleteAction";
import { type FilterFormData, ProxiesFilterForm } from "./ProxiesFilterForm";
import type { ProxyRow } from "./types";

const Proxies: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { getErrorInterface } = useApiError();

	const [batchUpdateDialog, setBatchUpdateDialog] = useState<boolean>(false);

	const [filters, setFilters] = useState<FilterFormData>({
		ip_address: "",
		port: null,
	});

	const server = useServerTableState();

	const handleSearch = useCallback(
		(values: FilterFormData) => {
			setFilters(values);
			server.onPageChange(0);
		},
		[server],
	);

	const rowSelection = useDataTableRowSelection<ProxyRow>(true);

	const {
		data: proxies,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useGetProxies({
		query: {
			page: server.pageIndex + 1,
			page_size: server.pageSize,
			ip_address: filters.ip_address ? filters.ip_address : undefined,
			port: filters.port,
		},
	});

	const columns = useMemo<ColumnDef<ProxyRow>[]>(
		() => [
			{
				id: "ip_address",
				header: t("proxy.tableIP"),
				enableSorting: false,
				cell: ({ row }) => row.original.ip_address,
			},
			{
				id: "port",
				header: t("proxy.tablePort"),
				enableSorting: false,
				cell: ({ row }) => row.original.port,
			},
			{
				id: "username",
				header: t("proxy.tableUsername"),
				enableSorting: false,
				cell: ({ row }) => row.original.username ?? "-",
			},
			{
				id: "password",
				header: t("proxy.tablePassword"),
				enableSorting: false,
				cell: ({ row }) => row.original.password ?? "-",
			},
			{
				id: "num_of_connected_senders",
				header: t("proxy.tableSenderCounts"),
				enableSorting: false,
				cell: ({ row }) => row.original.num_of_connected_senders,
			},
			{
				id: "actions",
				header: "",
				enableSorting: false,
				cell: ({ row }) => <ProxiesSingleAction selectedProxy={row.original} />,
			},
		],
		[t],
	);

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col-reverse items-stretch justify-between gap-4 md:flex-row md:items-center">
				<Typography variant="title-md" as="h1" className="mt-4 md:mt-0">
					{t("proxy.title")}
				</Typography>
				<div className="flex gap-4">
					<Button
						type="button"
						color="secondary"
						className="flex-1"
						onClick={() => setBatchUpdateDialog(true)}
					>
						{t("proxy.batchUpdateButton")}
					</Button>
					<Button
						type="button"
						className="flex-1 min-w-0"
						startIcon={<Icon name="add-line" />}
						onClick={() => navigate({ to: "/proxy/new" })}
					>
						{t("proxy.addProxyButton")}
					</Button>
				</div>
			</div>

			<ToolbarSwitcher
				hasSelection={rowSelection.selectedIds.length > 0}
				filterComponent={<ProxiesFilterForm onSearch={handleSearch} />}
				actionComponent={
					<ProxiesDeleteAction
						selectedIds={rowSelection.selectedIds}
						onClear={rowSelection.clear}
					/>
				}
			/>

			<DataTable
				columns={columns}
				data={proxies?.results ?? []}
				totalItems={proxies?.count ?? 0}
				server={server}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error ? getErrorInterface(error) : null}
				onRetry={refetch}
				getRowId={(row) => row.id}
				rowSelection={rowSelection}
			/>

			<ProxiesBatchUpdateDialog
				open={batchUpdateDialog}
				onClose={() => setBatchUpdateDialog(false)}
			/>
		</div>
	);
};

export default Proxies;
